import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { UseCaseFlowDef } from "./types";
import { nodeById } from "./flows";
import { getInternalSteps } from "./internalSteps";
import { iconForKind, kindAccent } from "./kindColors";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

type Props = {
  flow: UseCaseFlowDef;
  pathIndex: number;
  subStepIndex: number;
};

export default function StepKeyPanel({ flow, pathIndex, subStepIndex }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();

  const nodeId = flow.path[pathIndex] ?? flow.path[0];
  const node = nodeById(flow, nodeId);
  if (!node) return null;

  const accent = kindAccent(theme, node.kind);
  const label = t(`docs.useCaseFlows.nodes.${node.labelKey}`);
  const detail = t(`docs.useCaseFlows.details.${flow.id}.${nodeId}`, {
    defaultValue: t(`docs.useCaseFlows.details.${flow.id}.fallback`),
  });
  const steps = getInternalSteps(flow, node, t);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100%",
        minHeight: 0,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: (th) =>
            alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.08 : 0.04),
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {t("docs.useCaseFlows.detailTitle")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("docs.useCaseFlows.stepKeyHint")}
        </Typography>
      </Box>

      <Box
        key={`${nodeId}-${pathIndex}`}
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          p: 2,
          animation: `${fadeIn} 0.28s ease`,
        }}
        aria-live="polite"
      >
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mb: 1.75 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 700,
              bgcolor: accent,
              color: "#fff",
            }}
          >
            {pathIndex + 1}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.35 }}>
              <Icon sx={{ fontSize: 22, color: accent }}>{iconForKind(node.kind)}</Icon>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                {label}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t("docs.useCaseFlows.stepOf", {
                current: pathIndex + 1,
                total: flow.path.length,
                label: label,
              })}
            </Typography>
            {node.showRouter && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  fontWeight: 700,
                  color: accent,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  fontSize: 10,
                }}
              >
                {t("docs.useCaseFlows.router.badge")}
              </Typography>
            )}
          </Box>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.6, mb: 2 }}
        >
          {detail}
        </Typography>

        {steps.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                display: "block",
                mb: 1,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "text.secondary",
                fontSize: 10,
              }}
            >
              {t("docs.useCaseFlows.calloutTitle")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {steps.map((step, index) => {
                const isActive = index === subStepIndex;
                const isDone = index < subStepIndex;
                return (
                  <Box
                    key={step.id}
                    sx={{
                      px: 1.25,
                      py: 1,
                      borderRadius: 1.25,
                      border: "1px solid",
                      borderColor: isActive
                        ? accent
                        : isDone
                          ? alpha(theme.palette.success.main, 0.45)
                          : "divider",
                      bgcolor: isActive
                        ? alpha(accent, theme.palette.mode === "dark" ? 0.18 : 0.1)
                        : isDone
                          ? alpha(theme.palette.success.main, 0.06)
                          : "transparent",
                      transition: "border-color 0.2s ease, background-color 0.2s ease",
                      opacity: isActive ? 1 : isDone ? 0.92 : 0.78,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={isActive ? 700 : 600}
                      sx={{ lineHeight: 1.35, mb: 0.5 }}
                    >
                      {index + 1}. {step.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        lineHeight: 1.5,
                        fontWeight: isActive ? 500 : 400,
                      }}
                    >
                      {step.detail}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
